import React, { useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSeo, SITE_URL } from "@/lib/seo";

const COMPANY = {
  name: "Automovilística Faro S.L.",
  cif: "B36652170",
  address: "Centro Comercial 3 Plaza América, Local 12, 36211 Vigo (Pontevedra)",
  email: "aefaro@gmail.com",
  phone: "986 29 24 55",
};

const S = ({ h, children }) => (
  <section className="mt-8">
    <h2 className="font-display font-black text-xl sm:text-2xl">{h}</h2>
    <div className="mt-3 space-y-3 text-[var(--faro-text-soft)] leading-relaxed text-sm sm:text-base">{children}</div>
  </section>
);

const CONTENT = {
  "aviso-legal": {
    title: "Aviso legal",
    body: (
      <>
        <S h="1. Datos identificativos">
          <p>En cumplimiento de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de que este sitio web, autoescuelasfaro.com, es titularidad de <strong>{COMPANY.name}</strong>, con CIF {COMPANY.cif}, domicilio en {COMPANY.address}, teléfono {COMPANY.phone} y email <a href={`mailto:${COMPANY.email}`} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{COMPANY.email}</a>.</p>
          <p>Automovilística Faro S.L. opera bajo el nombre comercial "Autoescuelas Faro" y es centro de formación de conductores autorizado por la Dirección General de Tráfico (DGT), incluida la autorización para impartir cursos de sensibilización y reeducación vial.</p>
        </S>
        <S h="2. Condiciones de uso">
          <p>El acceso y uso de este sitio web atribuye la condición de usuario e implica la aceptación de las presentes condiciones. El usuario se compromete a hacer un uso adecuado de los contenidos y a no emplearlos para actividades ilícitas o contrarias a la buena fe.</p>
        </S>
        <S h="3. Propiedad intelectual">
          <p>Todos los contenidos de este sitio (textos, fotografías, logotipos, diseño) son titularidad de {COMPANY.name} o de terceros que han autorizado su uso, y están protegidos por la normativa de propiedad intelectual e industrial. Queda prohibida su reproducción sin autorización expresa.</p>
        </S>
        <S h="4. Responsabilidad">
          <p>La información sobre cursos, precios y fechas se mantiene actualizada con la mayor diligencia posible; no obstante, las condiciones definitivas se confirmarán siempre en el momento de la matrícula. {COMPANY.name} no se hace responsable del contenido de sitios de terceros enlazados desde esta web.</p>
        </S>
        <S h="5. Legislación aplicable">
          <p>Las presentes condiciones se rigen por la legislación española. Para cualquier controversia serán competentes los juzgados y tribunales de Vigo, salvo que la normativa de consumidores disponga otra cosa.</p>
        </S>
      </>
    ),
  },
  "politica-privacidad": {
    title: "Política de privacidad",
    body: (
      <>
        <S h="1. Responsable del tratamiento">
          <p><strong>{COMPANY.name}</strong> · CIF {COMPANY.cif} · {COMPANY.address} · <a href={`mailto:${COMPANY.email}`} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{COMPANY.email}</a> · {COMPANY.phone}.</p>
        </S>
        <S h="2. Qué datos tratamos y para qué">
          <p><strong>Formulario de contacto/matrícula:</strong> nombre, teléfono, email, centro y permiso de interés. Finalidad: atender tu solicitud de información o matrícula y contactar contigo.</p>
          <p><strong>Formulario de inscripción en cursos de puntos:</strong> nombre y apellidos, DNI/NIE, teléfono, dirección, código postal, fecha de nacimiento y, opcionalmente, fecha del primer carnet y email. Finalidad: reservar tu plaza, preparar la documentación exigida por la DGT para los cursos de sensibilización y reeducación vial, y contactar contigo para confirmar fechas.</p>
          <p><strong>Comunicación por WhatsApp:</strong> al enviar los formularios se abre una conversación de WhatsApp con la sede correspondiente para agilizar la atención. WhatsApp (Meta Platforms Ireland Ltd.) actúa como proveedor del servicio de mensajería con sus propias condiciones de privacidad.</p>
        </S>
        <S h="3. Legitimación">
          <p>La base legal es tu <strong>consentimiento</strong> (casilla de aceptación de los formularios) y, cuando reservas plaza, la <strong>ejecución de medidas precontractuales/contractuales</strong>. Los datos exigidos por la normativa de los cursos DGT se tratan además en cumplimiento de una <strong>obligación legal</strong>.</p>
        </S>
        <S h="4. Conservación y destinatarios">
          <p>Conservamos los datos mientras dure la relación y los plazos legales aplicables (normativa de tráfico, fiscal y mercantil). No cedemos datos a terceros salvo obligación legal (en particular, a la DGT/Jefatura Provincial de Tráfico para la tramitación de cursos y permisos).</p>
        </S>
        <S h="5. Tus derechos">
          <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a <a href={`mailto:${COMPANY.email}`} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{COMPANY.email}</a> o por correo postal a la dirección indicada, adjuntando copia de tu DNI. También puedes reclamar ante la Agencia Española de Protección de Datos (aepd.es).</p>
        </S>
        <S h="6. Seguridad">
          <p>Aplicamos medidas técnicas y organizativas apropiadas para proteger tus datos frente a accesos no autorizados, pérdida o alteración.</p>
        </S>
      </>
    ),
  },
  "politica-cookies": {
    title: "Política de cookies",
    body: (
      <>
        <S h="1. Qué son las cookies">
          <p>Las cookies son pequeños archivos que se almacenan en tu dispositivo al visitar una web. Sirven, entre otras cosas, para recordar preferencias o medir el uso del sitio.</p>
        </S>
        <S h="2. Cookies que utiliza este sitio">
          <p><strong>Técnicas (necesarias):</strong> imprescindibles para el funcionamiento del sitio (por ejemplo, recordar tu elección sobre cookies). No requieren consentimiento.</p>
          <p><strong>Analíticas (Google Analytics 4):</strong> solo se activan si las aceptas en el aviso de cookies. Nos ayudan a saber qué páginas se visitan más para mejorar el sitio. Proveedor: Google Ireland Ltd. Puedes consultar su política en policies.google.com.</p>
        </S>
        <S h="3. Cómo gestionar o revocar tu consentimiento">
          <p>Puedes aceptar o rechazar las cookies analíticas en el aviso que aparece en tu primera visita. Para cambiar tu elección, borra los datos de navegación de este sitio en tu navegador y vuelve a cargar la página: el aviso aparecerá de nuevo. También puedes bloquear las cookies desde la configuración de tu navegador.</p>
        </S>
        <S h="4. Responsable">
          <p>{COMPANY.name} · CIF {COMPANY.cif} · {COMPANY.address} · <a href={`mailto:${COMPANY.email}`} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">{COMPANY.email}</a>.</p>
        </S>
      </>
    ),
  },
};

export default function LegalPage({ slug }) {
  const page = CONTENT[slug];
  const otherPages = useMemo(() => Object.keys(CONTENT).filter((s) => s !== slug), [slug]);
  useSeo({
    title: `${page.title} | Autoescuelas Faro`,
    description: `${page.title} de autoescuelasfaro.com — ${COMPANY.name}, CIF ${COMPANY.cif}, Vigo.`,
    canonical: `${SITE_URL}/${slug}`,
  });

  useEffect(() => {
    const m = document.createElement("meta");
    m.setAttribute("name", "robots");
    m.setAttribute("content", "noindex,follow");
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  return (
    <div data-testid={`legal-page-${slug}`}>
      <Header />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <span className="eyebrow">Información legal</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">{page.title}</h1>
        <p className="mt-3 text-sm text-[var(--faro-text-soft)]">Última actualización: agosto de 2026</p>
        {page.body}
        <div className="mt-10 flex flex-wrap gap-4 text-sm">
          {otherPages.map((s) => (
            <Link key={s} to={`/${s}`} className="text-[var(--faro-blue)] font-bold underline underline-offset-2">
              {CONTENT[s].title}
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
